"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { ArrowLeft, Search, User, BookOpen } from "lucide-react"
import { Input } from "../ui/input"
import { adminDashboard } from '../../services/api'

// Types
interface Student {
  id: number
  fullName: string
  email: string
}

interface Formation {
  id: number
  name: string
}

export default function AdminAddInscription() {
  const [students, setStudents] = useState<Student[]>([])
  const [formations, setFormations] = useState<Formation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const [selectedFormation, setSelectedFormation] = useState<string>("")
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")

  // Charger les données du formulaire
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true)
        const response = await adminDashboard.getInscriptionFormData()
        if (response.data.success) {
          setStudents(response.data.users)
          setFormations(response.data.formations)
        }
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err)
        setError('Erreur lors du chargement des données du formulaire')
      } finally {
        setLoading(false)
      }
    }

    fetchFormData()
  }, [])

  // Filter students based on search term
  const filteredStudents = students.filter((student) =>
    searchTerm === ""
      ? false
      : // Don't show any students if search is empty
        student.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.id.toString().includes(searchTerm),
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedStudent) {
      alert("Veuillez sélectionner un étudiant")
      return
    }

    if (!selectedFormation) {
      alert("Veuillez sélectionner une formation")
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await adminDashboard.createInscription(selectedStudent.id, Number(selectedFormation))
      
      setSuccessMessage(`Inscription créée avec succès pour ${selectedStudent.fullName}!`)
      setShowSuccessPopup(true)
      
      // Reset form
      setSelectedStudent(null)
      setSelectedFormation("")
      setSearchTerm("")
      
      // Auto-hide the popup after 4 seconds
      setTimeout(() => {
        setShowSuccessPopup(false)
        // Navigate back to inscriptions list
        window.location.href = '/admin/inscriptions'
      }, 4000)
      
    } catch (err) {
      console.error('Erreur lors de la création de l\'inscription:', err)
      setError('Erreur lors de la création de l\'inscription')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    window.location.href = '/admin/inscriptions'
  }

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold tracking-tight">Nouvelle inscription</h1>
            <p className="text-gray-500 dark:text-gray-400">Attribuez une formation à un étudiant</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Carte de sélection d'étudiant (gauche) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Sélection de l'étudiant
                </CardTitle>
                <CardDescription>Recherchez et sélectionnez un étudiant</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="studentSearch">Rechercher un étudiant</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <Input
                        id="studentSearch"
                        type="search"
                        placeholder="Nom, email ou identifiant..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
                    {filteredStudents.map((student) => (
                      <div
                        key={student.id}
                        className={`p-3 flex items-center justify-between hover:bg-muted cursor-pointer ${
                          selectedStudent?.id === student.id ? "bg-muted" : ""
                        }`}
                        onClick={() => setSelectedStudent(student)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            {student.fullName.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium">{student.fullName}</p>
                            <p className="text-sm text-muted-foreground">{student.email}</p>
                          </div>
                        </div>
                        {selectedStudent?.id === student.id && <div className="h-2 w-2 rounded-full bg-primary"></div>}
                      </div>
                    ))}

                    {searchTerm && filteredStudents.length === 0 && (
                      <div className="p-4 text-center text-muted-foreground">
                        Aucun étudiant trouvé. Veuillez modifier votre recherche.
                      </div>
                    )}

                    {!searchTerm && (
                      <div className="p-4 text-center text-muted-foreground">
                        Commencez à taper pour rechercher un étudiant.
                      </div>
                    )}
                  </div>

                  {selectedStudent && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                        <span className="text-sm font-medium text-green-700">
                          Étudiant sélectionné: {selectedStudent.fullName}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Carte de sélection de formation (droite) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Sélection de la formation
                </CardTitle>
                <CardDescription>Choisissez la formation à attribuer</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="course">Formation</Label>
                    <Select 
                      value={selectedFormation} 
                      onValueChange={setSelectedFormation}
                      required
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sélectionnez une formation" />
                      </SelectTrigger>
                      <SelectContent>
                        {formations.map((formation) => (
                          <SelectItem key={formation.id} value={formation.id.toString()}>
                            {formation.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedFormation && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        <span className="text-sm font-medium text-blue-700">
                          Formation sélectionnée: {formations.find(f => f.id.toString() === selectedFormation)?.name}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-8 mt-4">
                    <Button variant="outline" type="button" onClick={handleBack}>
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={isSubmitting || !selectedStudent || !selectedFormation}
                    >
                      {isSubmitting ? "Inscription en cours..." : "Inscrire l'étudiant"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
      
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full mx-4 animate-in fade-in duration-300">
            <div className="flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-1">Succès</h3>
              <p className="text-gray-500 dark:text-gray-400">{successMessage}</p>
              <Button 
                className="mt-4" 
                onClick={() => {
                  setShowSuccessPopup(false)
                  window.location.href = '/admin/inscriptions'
                }}
              >
                Retour à la liste
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}